/**
 * `/api/claims` payloads — reward calculation, claim submission and scheduling.
 *
 * Every response here keeps the Next.js envelope byte-for-byte: the calculation
 * endpoints return a fully zero-filled object *plus* an `error` string on
 * failure rather than a bare `{ error }`, because the claim UI reads the numeric
 * fields unconditionally and a missing key renders `NaN`.
 */
import { z } from "zod";

import {
  baseUnitsSchema,
  claimStatusSchema,
  signedRequestEnvelopeSchema,
  suiAddressSchema,
  tokenAmountSchema,
} from "../primitives";

/** Query for `GET /api/claims/calculate-leasing-rewards`. */
export const calculateLeasingRewardsQuerySchema = z.object({
  user_address: suiAddressSchema,
  /** Coerced: query strings are always strings, and the handler compares numbers. */
  lease_id: z.coerce.number().int().positive(),
});

/** Resolved leasing-rewards query. */
export type CalculateLeasingRewardsQuery = z.infer<typeof calculateLeasingRewardsQuerySchema>;

/**
 * Per-lease reward breakdown.
 *
 * `isClaimingEnabled` is the composite of the global kill switch, the TGE date,
 * lease maturity and "no claim already queued for this lease" — the client must
 * not re-derive it.
 */
export const calculateLeasingRewardsResponseSchema = z.object({
  totalClaimed: tokenAmountSchema,
  totalRewards: tokenAmountSchema,
  lockedRewards: tokenAmountSchema,
  unlockedRewards: tokenAmountSchema,
  burnPenalty: tokenAmountSchema,
  isClaimingEnabled: z.boolean(),
  /**
   * The most one claim may request, in base units — the smaller of the claim
   * batcher's `MAX_PAYOUT_PER_RECIPIENT` and `MAX_BATCH_TOTAL`. Null when the API
   * has neither configured. A claim above it is refused by the POST route; the
   * batcher would otherwise quarantine it.
   *
   * Defaults to null so a client deployed ahead of the API still parses the
   * older reply.
   */
  maxClaimUnits: baseUnitsSchema.nullable().default(null),
  error: z.string().optional(),
});

/** Resolved leasing-rewards response. */
export type CalculateLeasingRewardsResponse = z.infer<typeof calculateLeasingRewardsResponseSchema>;

/** Query for `GET /api/claims/calculate-unlocked-rewards`. */
export const calculateUnlockedRewardsQuerySchema = z.object({
  user_address: suiAddressSchema,
});

/** Resolved unlocked-rewards query. */
export type CalculateUnlockedRewardsQuery = z.infer<typeof calculateUnlockedRewardsQuerySchema>;

/**
 * Daily-rewards breakdown for a wallet.
 *
 * Invariant the server maintains: `lockedRewards + unlockedRewards +
 * totalClaimed === totalRewards`. The dashboard's "Total Tokens" tile adds the
 * three, so a violation shows up as an overstated balance.
 */
export const calculateUnlockedRewardsResponseSchema = z.object({
  totalClaimed: tokenAmountSchema,
  totalRewards: tokenAmountSchema,
  lockedRewards: tokenAmountSchema,
  unlockedRewards: tokenAmountSchema,
  burnPenalty: tokenAmountSchema,
  isClaimingEnabled: z.boolean(),
  hasSnapshot: z.boolean(),
  /**
   * The most one claim may request, in base units — the smaller of the claim
   * batcher's `MAX_PAYOUT_PER_RECIPIENT` and `MAX_BATCH_TOTAL`. Null when the API
   * has neither configured. A claim above it is refused by the POST route; the
   * batcher would otherwise quarantine it.
   *
   * Defaults to null so a client deployed ahead of the API still parses the
   * older reply.
   */
  maxClaimUnits: baseUnitsSchema.nullable().default(null),
  error: z.string().optional(),
});

/** Resolved unlocked-rewards response. */
export type CalculateUnlockedRewardsResponse = z.infer<
  typeof calculateUnlockedRewardsResponseSchema
>;

/** Query for `GET /api/claims/get-by-user-address`. */
export const getClaimsByUserAddressQuerySchema = z.object({
  user_address: suiAddressSchema,
});

/** Resolved claims-by-address query. */
export type GetClaimsByUserAddressQuery = z.infer<typeof getClaimsByUserAddressQuerySchema>;

/**
 * One `daily_rewards_claims` row as JSON.
 *
 * Money columns stay strings: they are Postgres NUMERIC and the only lossless
 * representation over JSON. Timestamps are ISO-8601 strings — handlers convert
 * the driver's `Date` explicitly, because a `Date` would fail this schema
 * (response validation runs before serialization).
 */
export const claimRecordSchema = z.object({
  id: z.number().int(),
  user_address: z.string(),
  claimed_at: z.string().nullable(),
  total_claimed: z.string(),
  total_requested: z.string(),
  total_locked: z.string(),
  total_unlocked: z.string(),
  burn_penalty: z.string(),
  daily_rewards_snapshot_id: z.number().int().nullable(),
  license_leases_id: z.number().int().nullable(),
  sui_transaction_digest: z.string().nullable(),
  status: claimStatusSchema,
  status_reason: z.string().nullable(),
  scheduled_claim_date: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

/** Resolved claim row. */
export type ClaimRecord = z.infer<typeof claimRecordSchema>;

/** Response for `GET /api/claims/get-by-user-address`. */
export const getClaimsByUserAddressResponseSchema = z.object({
  claims: z.array(claimRecordSchema),
  error: z.string().optional(),
});

/** Resolved claims-by-address response. */
export type GetClaimsByUserAddressResponse = z.infer<typeof getClaimsByUserAddressResponseSchema>;

/**
 * Per-burn-tier claim counters.
 *
 * `quarantined` is part of the runtime shape even though the Next.js
 * `BurnPenaltyStats` type omitted it — the model always counted it and the JSON
 * always carried it. Declaring it here is what makes the two agree.
 */
export const burnPenaltyStatsSchema = z.object({
  count: z.object({
    pending: z.number().int(),
    processing: z.number().int(),
    completed: z.number().int(),
    quarantined: z.number().int(),
    total: z.number().int(),
  }),
  historical: z.object({
    requested: z.number(),
    sent: z.number(),
  }),
});

/** Resolved per-tier counters. */
export type BurnPenaltyStats = z.infer<typeof burnPenaltyStatsSchema>;

/**
 * Response for `GET /api/claims/stats` (admin, API-key guarded).
 *
 * The three keys are the burn-penalty ladder as percentages-over-100, kept as
 * literal strings because the admin dashboard indexes them by that exact text.
 */
export const getClaimsStatsResponseSchema = z.object({
  allocated: z.object({
    totalClaimed: z.number(),
    totalRequested: z.number(),
  }),
  burnPenaltyStats: z.object({
    "50/100": burnPenaltyStatsSchema,
    "30/100": burnPenaltyStatsSchema,
    "0/100": burnPenaltyStatsSchema,
  }),
  error: z.string().optional(),
});

/** Resolved claim-stats response. */
export type GetClaimsStatsResponse = z.infer<typeof getClaimsStatsResponseSchema>;

/** Body for `POST /api/claims/daily-rewards`. */
export const claimDailyRewardsRequestSchema = signedRequestEnvelopeSchema.extend({
  user_address: suiAddressSchema,
  /** What the user asks for. Never trusted: the server recomputes and compares. */
  amount: tokenAmountSchema,
});

/** Resolved daily-rewards claim body. */
export type ClaimDailyRewardsRequest = z.infer<typeof claimDailyRewardsRequestSchema>;

/** Body for `POST /api/claims/leasing-rewards`. */
export const claimLeasingRewardsRequestSchema = signedRequestEnvelopeSchema.extend({
  user_address: suiAddressSchema,
  lease_id: z.number().int().positive(),
  amount: tokenAmountSchema,
});

/** Resolved leasing-rewards claim body. */
export type ClaimLeasingRewardsRequest = z.infer<typeof claimLeasingRewardsRequestSchema>;

/**
 * Reservation result shared by both claim endpoints.
 *
 * `claim_id` was returned by the Next.js daily-rewards route even though its
 * local type omitted it; it is declared here so the contract matches the wire.
 */
export const claimSubmitResponseSchema = z.object({
  success: z.boolean(),
  claim_id: z.number().int().optional(),
  status: z.string().optional(),
  total_requested: tokenAmountSchema.optional(),
  transaction_digest: z.string().optional(),
  error: z.string().optional(),
});

/** Resolved claim-submission response. */
export type ClaimSubmitResponse = z.infer<typeof claimSubmitResponseSchema>;

/**
 * The two schedulable days after TGE. Day 32 costs a 30% burn, day 94 costs
 * nothing — which is exactly why the signed message has to bind the day.
 */
export const scheduleDayNumberSchema = z.union([z.literal(32), z.literal(94)]);

/** Body for `POST /api/claims/schedule-claim`. */
export const scheduleClaimRequestSchema = signedRequestEnvelopeSchema.extend({
  user_address: suiAddressSchema,
  scheduleDayNumber: scheduleDayNumberSchema,
});

/** Resolved schedule-claim body. */
export type ScheduleClaimRequest = z.infer<typeof scheduleClaimRequestSchema>;

/** Response for `POST /api/claims/schedule-claim`. */
export const scheduleClaimResponseSchema = z.object({
  success: z.boolean(),
  claim_id: z.number().int().optional(),
  scheduled_date: z.string().optional(),
  amount: tokenAmountSchema.optional(),
  burn_penalty: z.number().optional(),
  error: z.string().optional(),
});

/** Resolved schedule-claim response. */
export type ScheduleClaimResponse = z.infer<typeof scheduleClaimResponseSchema>;
